const DEBUG_STORAGE_KEY = 'DEBUG_GLOBAL_E';
const DEBUG_QUERY_PARAM = 'debugGlobalE';

export const isGlobalEDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (window.localStorage.getItem(DEBUG_STORAGE_KEY) === '1') {
    return true;
  }

  return new URLSearchParams(window.location.search).has(DEBUG_QUERY_PARAM);
};

const readGlobalECartIdSnapshot = (): string | undefined => {
  const match = document.cookie.match(/(?:^|;)\s*GlobalECartId=([^;]*)/);

  if (!match) {
    return undefined;
  }

  try {
    return unescape(match[1]);
  } catch {
    return match[1];
  }
};

const listGlobalECartIdSnapshot = (): string[] =>
  Array.from(
    document.cookie.matchAll(/(?:^|;)\s*GlobalECartId=([^;]*)/g),
    (match) => {
      try {
        return unescape(match[1]);
      } catch {
        return match[1];
      }
    },
  );

export const logGlobalEDebug = (
  event: string,
  data?: Record<string, unknown>,
): void => {
  if (!isGlobalEDebugEnabled()) {
    return;
  }

  console.warn('[GlobalE]', event, {
    pathname: window.location.pathname,
    geCartIdFirst: readGlobalECartIdSnapshot(),
    geCartIdAll: listGlobalECartIdSnapshot(),
    rawCookie: document.cookie,
    loadScript: (window as Window & { __globalELoadScript?: boolean })
      .__globalELoadScript,
    hasGeCheckout: Boolean(document.getElementById('ge-checkout')),
    hasGemScript: Boolean(
      document.querySelector('script[src*="/includes/js/"]'),
    ),
    hasGlegem: Boolean((window as Window & { glegem?: unknown }).glegem),
    ...data,
  });
};

let debugInitialized = false;
let networkHookInstalled = false;

const shouldLogGlobalENetworkUrl = (url: string): boolean =>
  url.includes('ge_ApiCall') || url.includes('/undefined/');

export const installGlobalEDebugNetworkHook = (): void => {
  if (typeof window === 'undefined' || !isGlobalEDebugEnabled()) {
    return;
  }

  if (networkHookInstalled) {
    return;
  }

  networkHookInstalled = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    let url = '';

    if (typeof input === 'string') {
      url = input;
    } else if (input instanceof URL) {
      url = input.toString();
    } else {
      url = input.url;
    }

    if (shouldLogGlobalENetworkUrl(url)) {
      logGlobalEDebug('network:fetch', { url });
    }

    return originalFetch(input, init);
  }) as typeof window.fetch;

  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function open(
    this: XMLHttpRequest,
    method: string,
    url: string | URL,
    isAsync?: boolean,
    username?: string | null,
    password?: string | null,
  ) {
    const resolvedUrl = String(url);

    if (shouldLogGlobalENetworkUrl(resolvedUrl)) {
      logGlobalEDebug('network:xhr', { method, url: resolvedUrl });
    }

    originalOpen.call(this, method, url, isAsync ?? true, username, password);
  } as typeof XMLHttpRequest.prototype.open;

  logGlobalEDebug('network-hook:installed');
};

const installGlobalEDebugResourceObserver = (): void => {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
    return;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (!shouldLogGlobalENetworkUrl(entry.name)) {
          return;
        }

        logGlobalEDebug('network:resource', {
          url: entry.name,
          initiatorType: (entry as PerformanceResourceTiming).initiatorType,
        });
      });
    });

    observer.observe({ type: 'resource', buffered: true });
    logGlobalEDebug('resource-observer:installed');
  } catch {
    return;
  }
};

export const initGlobalEDebug = (): void => {
  if (
    typeof window === 'undefined' ||
    !isGlobalEDebugEnabled() ||
    debugInitialized
  ) {
    return;
  }

  debugInitialized = true;
  installGlobalEDebugNetworkHook();
  installGlobalEDebugResourceObserver();
  logGlobalEDebug('debug:boot');
};
